import { useEffect, useMemo, useState } from 'react';
import { NavigationHeader } from '@/components/NavigationHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { auth, storage, db } from '@/firebase';
import { listInstitutions, listCouncilors, updateInstitution, amendmentsCol, listAmendmentsByInstitutionIds, restGetUserMapping, restListAmendmentsByInstitutionIds, getInstitutionGroupIdsByCnpj, getUserCanUploadImages, logUploadAttempt, getSettings, type Institution, type Amendment, type Settings } from '@/services/firestore';
import { adminHealth } from '@/services/admin-api';
import * as FirestoreService from '@/services/firestore';
import { getUserInstitutionId } from '@/services/firestore';
import { getCategoryColors } from '@/lib/category-colors';
import { formatIsoDateToPtBr, isNowPastScheduleCuiaba } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, getIdTokenResult } from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { onSnapshot, query, where, setDoc, doc } from 'firebase/firestore';

export default function InstitutionArea() {
  const [institutionId, setInstitutionId] = useState<string>('');
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [councilorById, setCouncilorById] = useState<Record<string, string>>({});
  const [institutionById, setInstitutionById] = useState<Record<string, Institution>>({});
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [link, setLink] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialX, setSocialX] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialWhatsapp, setSocialWhatsapp] = useState('');
  const [selectedTab, setSelectedTab] = useState<'dados' | 'emendas' | 'seguranca'>('emendas');
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const { toast } = useToast();
  const adminBase = import.meta.env.DEV ? '/admin-api' : ((import.meta.env.VITE_ADMIN_SERVER_URL as string) || '');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailForId, setDetailForId] = useState<string | null>(null);
  const [detailObject, setDetailObject] = useState('');
  const [planProjectName, setPlanProjectName] = useState('');
  const [planPurpose, setPlanPurpose] = useState('');
  const [planPeriodMode, setPlanPeriodMode] = useState<'ano' | 'meses'>('ano');
  const [planMonths, setPlanMonths] = useState('');
  const [classification, setClassification] = useState<'Custeio' | 'Investimento' | ''>('');
  const [attachmentsUploading, setAttachmentsUploading] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [detailSuccess, setDetailSuccess] = useState(false);
  const [detailDisabled, setDetailDisabled] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [detailingSaved, setDetailingSaved] = useState(false);
  const [dataStatus, setDataStatus] = useState<'ok' | 'no-results' | 'connection-error'>('ok');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [sysSettings, setSysSettings] = useState<Settings | null>(null);

  // Calcula estado do prazo
  const schedule = sysSettings?.institutionFormSchedule;
  const isScheduleEnabled = schedule?.enabled;

  // Exceção de prazo para instituição específica
  const exemptCnpjs = ['18.194.099/0001-75'];
  const currentCnpj = (institution?.cnpj || '').replace(/\D/g, '');
  const isExempt = exemptCnpjs.some(c => c.replace(/\D/g, '') === currentCnpj);

  const deadlineDatePassed = isScheduleEnabled ? isNowPastScheduleCuiaba(schedule.endDate, schedule.endTime) : false;
  const isDeadlinePassed = deadlineDatePassed && !isExempt;

  const deadlineLabel = isScheduleEnabled ? `${formatIsoDateToPtBr(schedule.endDate)} às ${schedule.endTime}` : '';

  const isDetailComplete = (a: Amendment | null | undefined) => {
    if (!a) return false;
    const obj = (a.objectDetail || '').trim();
    const wp = a.workPlan || {};
    const proj = (wp.projectName || '').trim();
    const purp = (wp.purpose || '').trim();
    const per = (wp.period || '').trim();
    if (!obj || obj.length < 3 || obj.length > 400) return false;
    if (!proj || proj.length < 3 || proj.length > 40) return false;
    if (!purp || purp.length < 3 || purp.length > 400) return false;
    if (!per || per.length < 3) return false;
    if (!a.classification) return false;
    return true;
  };

  useEffect(() => {
    (async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }
      let insts: Institution[] = [];
      let couns: any[] = [];
      let sets: Settings | null = null;
      try {
        [insts, couns, sets] = await Promise.all([listInstitutions(), listCouncilors(), getSettings()]);
        setSysSettings(sets);
      } catch {
        setDataStatus('connection-error');
      }
      let instId = await getUserInstitutionId(user.uid);
      if (!instId) {
        const rest = await restGetUserMapping(user.uid);
        instId = rest?.institutionId || '';
      }
      if (!instId) {
        try {
          const token = await getIdTokenResult(user);
          instId = (token.claims as any)?.institutionId || '';
        } catch { }
      }
      if (!instId && user.email) {
        const match = (user.email || '').match(/cnpj-(\d{14})@instituicao\.local/i);
        const digits = match ? match[1] : undefined;
        if (digits) {
          const found = insts.find(i => (i.cnpj || '').replace(/\D/g, '') === digits);
          instId = found?.id || '';
        }
      }
      if (!instId) { setLoading(false); setDataStatus('connection-error'); return; }
      setInstitutionId(instId);
      const inst = insts.find(i => i.id === instId) || null;
      setInstitution(inst);
      setInstitutionById(Object.fromEntries(insts.map(i => [i.id, i])));
      setContactEmail(inst?.contact?.email || '');
      setContactPhone(inst?.contact?.phone || '');
      setLink(inst?.link || '');
      setLogoUrl(inst?.logoUrl || '');
      setSocialFacebook(inst?.social?.facebook || '');
      setSocialInstagram(inst?.social?.instagram || '');
      setSocialX(inst?.social?.x || '');
      setSocialTiktok(inst?.social?.tiktok || '');
      setSocialYoutube(inst?.social?.youtube || '');
      setSocialWhatsapp(inst?.social?.whatsapp || '');
      const groupIds = inst ? getInstitutionGroupIdsByCnpj(insts, inst.cnpj) : [instId];
      try {
        const sdk = await listAmendmentsByInstitutionIds(groupIds);
        if (sdk.length > 0) {
          setAmendments(sdk);
          setDataStatus('ok');
        } else {
          try {
            const rest = await restListAmendmentsByInstitutionIds(groupIds);
            setAmendments(rest);
            setDataStatus(rest.length > 0 ? 'ok' : 'no-results');
          } catch {
            setAmendments([]);
            setDataStatus('connection-error');
          }
        }
      } catch {
        try {
          const rest = await restListAmendmentsByInstitutionIds(groupIds);
          setAmendments(rest);
          setDataStatus(rest.length > 0 ? 'ok' : 'no-results');
        } catch {
          setAmendments([]);
          setDataStatus('connection-error');
        }
      }
      setCouncilorById(Object.fromEntries(couns.map(c => [c.id, c.name])));
      setLoading(false);
      try {
        const unsubs: Array<() => void> = [];
        const uniq = Array.from(new Set(groupIds));
        const batch = (arr: string[], size: number) => Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));
        for (const part of batch(uniq, 10)) {
          const q = query(amendmentsCol, where('institutionId', 'in', part));
          const unsub = onSnapshot(q, (snap) => {
            const all = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
            setAmendments(prev => {
              const map = new Map<string, Amendment>();
              for (const p of prev) { if (uniq.includes(p.institutionId)) map.set(p.id, p); }
              for (const x of all) map.set(x.id, x as Amendment);
              return Array.from(map.values());
            });
          });
          unsubs.push(unsub);
        }
        return () => { for (const u of unsubs) { try { u(); } catch { } } };
      } catch { }
    })();
  }, []);

  useEffect(() => {
    const must = localStorage.getItem('forcePasswordChange') === 'true';
    if (must) setSelectedTab('seguranca');
  }, []);

  useEffect(() => {
    if (!detailOpen || !detailForId) return;
    const a = amendments.find(x => x.id === detailForId);
    setDetailObject(a?.objectDetail || '');
    setPlanProjectName(a?.workPlan?.projectName || '');
    setPlanPurpose(a?.workPlan?.purpose || '');
    const per = a?.workPlan?.period || '';
    if (per && per.startsWith('2026') && per.includes('(')) {
      setPlanPeriodMode('meses');
      const inside = per.replace(/^2026\s*\(/, '').replace(/\)$/, '');
      setPlanMonths(inside);
    } else {
      setPlanPeriodMode('ano');
      setPlanMonths('');
    }
    setAttachments(a?.attachments || []);
    setClassification((a?.classification as any) || '');
    setDetailSuccess(false);

    // Check if detailing is already saved/locked
    // Se foi devolvida para retificação, desbloquear para edição
    const isSaved = !!(a as any).detailingSaved && a.dispatchStatus !== 'retificar';
    setDetailingSaved(isSaved);
    setDetailDisabled(isSaved);
  }, [detailOpen, detailForId, amendments]);

  const visibleAmendments = useMemo(() => {
    const norm = (s: string) => s.trim();
    const q = search.toLowerCase();
    return amendments.filter(a => {
      const instCat = institutionById[a.institutionId]?.category || institution?.category || '—';
      const cat = norm(a.category || instCat || '—');
      const matchCat = selectedCategory ? cat === norm(selectedCategory) : true;
      const matchSearch = q ? ((a.description || '').toLowerCase().includes(q)) : true;
      return matchCat && matchSearch;
    });
  }, [amendments, search, selectedCategory, institutionById, institution]);

  const totals = useMemo(() => {
    const sumCents = visibleAmendments.reduce((acc, a) => acc + Math.round(((a.value || 0) as number) * 100), 0);
    const sum = sumCents / 100;
    return { count: visibleAmendments.length, sum };
  }, [visibleAmendments]);

  const filtered = visibleAmendments;

  const categories = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    const norm = (s: string) => s.trim();
    amendments.forEach(a => {
      const instCat = institutionById[a.institutionId]?.category || institution?.category || '—';
      const cat = a.category || instCat || '—';
      const key = norm(cat);
      const name = getCategoryColors(cat).name;
      const cur = map.get(key);
      map.set(key, { name, count: (cur?.count || 0) + 1 });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [amendments, institutionById, institution]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-inter">
        <NavigationHeader userType="instituicao" userName={institution?.name || undefined} />
        <div className="max-w-5xl mx-auto p-6">
          <p className="text-muted-foreground">Carregando…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-inter">
      <NavigationHeader userType="instituicao" userName={institution?.name || undefined} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <header className="mb-6">
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-16 w-16 sm:h-20 sm:w-20 rounded object-contain" />
            ) : null}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-foreground">
                {!logoUrl ? <i className="fas fa-building text-primary mr-2" /> : null}
                Painel da Instituição
              </h1>
              {institution && (
                <p className="text-muted-foreground truncate">
                  {institution.name} {institution.category ? `• ${institution.category}` : ''} • CNPJ: {institution.cnpj || '—'}
                </p>
              )}
            </div>
          </div>
        </header>

        {/* Banner de Status (Retificação ou Prazo) */}
        {amendments.some(a => a.dispatchStatus === 'retificar') ? (
          <div className="mb-6 p-4 rounded-lg border bg-amber-50 border-amber-200 text-amber-900 flex flex-col md:flex-row items-start md:items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 mt-1 md:mt-0">
              <i className="fas fa-exclamation-triangle text-xl text-amber-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">Atenção: Emendas Aguardando Retificação</h3>
              <p className="text-sm font-medium mb-1">
                As seguintes emendas foram devolvidas pelo gestor e estão <strong>liberadas para alteração</strong> (mesmo fora do prazo):
              </p>
              <ul className="list-disc list-inside text-sm pl-2 mt-1 space-y-1">
                {amendments.filter(a => a.dispatchStatus === 'retificar').map(a => (
                  <li key={a.id}>
                    <strong>Emenda {a.officialCode || a.id}</strong>: {a.description}
                    {a.dispatchNote && <span className="text-amber-800/80"> — Motivo: {a.dispatchNote}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          isScheduleEnabled && (
            <div className={`mb-6 p-4 rounded-lg border flex flex-col md:flex-row items-center gap-3 ${isDeadlinePassed
              ? 'bg-destructive/10 border-destructive text-destructive'
              : (deadlineDatePassed && isExempt ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800')
              }`}>
              <div className={`p-2 rounded-full ${isDeadlinePassed ? 'bg-destructive/20' : (deadlineDatePassed && isExempt ? 'bg-blue-100' : 'bg-amber-100')}`}>
                <i className={`fas ${isDeadlinePassed ? 'fa-lock' : (deadlineDatePassed && isExempt ? 'fa-unlock' : 'fa-clock')} text-xl`}></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-bold text-lg">
                  {isDeadlinePassed ? 'Prazo Encerrado' : (deadlineDatePassed && isExempt ? 'Prazo Estendido (Exceção)' : 'Prazo para Detalhamento')}
                </h3>
                <p className="text-sm font-medium">
                  {isDeadlinePassed
                    ? `O prazo para envio das declarações encerrou em ${deadlineLabel}. O preenchimento está bloqueado para novas emendas.`
                    : (deadlineDatePassed && isExempt
                      ? 'Seu prazo foi estendido administrativamente. Você pode realizar o detalhamento.'
                      : `O prazo para detalhar as emendas encerra em ${deadlineLabel}. Evite deixar para a última hora.`
                    )
                  }
                </p>
              </div>
            </div>
          )
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1" aria-label="Seções">
            <nav className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedTab('dados')}
                aria-selected={selectedTab === 'dados'}
                className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium border ${selectedTab === 'dados' ? 'bg-accent text-accent-foreground border-primary' : 'text-foreground hover:bg-accent border-transparent'}`}
              >
                <i className="fas fa-id-card mr-2" /> Dados da instituição
              </button>
              <button
                type="button"
                onClick={() => setSelectedTab('emendas')}
                aria-selected={selectedTab === 'emendas'}
                className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium border ${selectedTab === 'emendas' ? 'bg-accent text-accent-foreground border-primary' : 'text-foreground hover:bg-accent border-transparent'}`}
              >
                <i className="fas fa-file-alt mr-2" /> Emendas recebidas
                <span className="ml-2 inline-block text-xs bg-black/10 rounded px-1">{totals.count}</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedTab('seguranca')}
                aria-selected={selectedTab === 'seguranca'}
                className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium border ${selectedTab === 'seguranca' ? 'bg-accent text-accent-foreground border-primary' : 'text-foreground hover:bg-accent border-transparent'}`}
              >
                <i className="fas fa-shield-alt mr-2" /> Segurança
              </button>
            </nav>
          </aside>

          <div className="lg:col-span-3 space-y-6">
            {selectedTab === 'emendas' && (
              <Card>
                <CardHeader>
                  <CardTitle>Resumo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 border rounded">
                      <p className="text-sm text-muted-foreground">Emendas recebidas</p>
                      <p className="text-2xl font-semibold">{totals.count}</p>
                    </div>
                    <div className="p-4 border rounded">
                      <p className="text-sm text-muted-foreground">Valor total</p>
                      <p className="text-2xl font-semibold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.sum)}
                      </p>
                    </div>
                    <div className="p-4 border rounded">
                      <p className="text-sm text-muted-foreground">Categorias do CNPJ</p>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 auto-rows-fr items-stretch">
                        {categories.map(c => {
                          const spec = getCategoryColors(c.key);
                          return (
                            <button
                              key={c.key}
                              type="button"
                              onClick={() => setSelectedCategory(c.key)}
                              onDoubleClick={() => setSelectedCategory(null)}
                              className={`relative inline-flex items-center justify-center rounded-xl border bg-background hover:bg-accent text-foreground w-10 h-10 transition-colors ${selectedCategory === c.key ? 'ring-2 ring-primary scale-[1.02] bg-accent' : ''}`}
                              aria-label={`${spec.name}: ${c.count} emendas`}
                            >
                              <i className={`fas ${spec.icon || 'fa-tag'} ${spec.textClass}`} />
                              <span className={`absolute -right-1 -bottom-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-white ${spec.textClass} ${spec.borderClass} text-xs`}>{c.count}</span>
                            </button>
                          );
                        })}
                        {categories.length === 0 && (
                          <p className="text-xs text-muted-foreground">Nenhuma categoria</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedTab === 'emendas' && (
              <Card id="emendas">
                <CardHeader>
                  <CardTitle>Emendas recebidas (consolidado por CNPJ)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      id="inst-search"
                      placeholder="Buscar por descrição"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Buscar emendas por descrição"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const csv = [
                          ['Descrição', 'Valor', 'Data'].join(','),
                          ...filtered.map(a => [
                            a.description?.replace(/"/g, '""') || '',
                            String(a.value || 0),
                            a.date || '',
                          ].map(cell => (cell.includes(',') || cell.includes('"')) ? `"${cell}"` : cell).join(',')),
                        ].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `emendas-${institutionId}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <i className="fas fa-file-csv mr-2" />
                      Exportar CSV
                    </Button>
                  </div>
                  {dataStatus === 'connection-error' ? (
                    <p className="text-red-600">Falha ao carregar emendas. Verifique sua conexão e tente novamente.</p>
                  ) : filtered.length === 0 ? (
                    <p className="text-muted-foreground">Nenhuma emenda encontrada.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                      {filtered.map((a) => {
                        const inst = institutionById[a.institutionId] || institution || undefined;
                        const cat = a.category || institutionById[a.institutionId]?.category || inst?.category;
                        const colors = getCategoryColors(cat);
                        const year = (a.date || '').slice(0, 4) || '—';
                        const councilorName = councilorById[a.councilorId] || '—';
                        return (
                          <div
                            key={a.id}
                            className={`border rounded-lg p-3 bg-card text-card-foreground transition-shadow hover:shadow-md cursor-pointer h-full flex flex-col min-h-[160px]`}
                            onClick={() => { setDetailForId(a.id); setDetailOpen(true); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setDetailForId(a.id); setDetailOpen(true); } }}
                          >
                            <div className="flex items-center mb-2">
                              <div className={`p-2 rounded-lg mr-2 border ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}>
                                <i className={`fas ${colors.icon || 'fa-tags'} text-lg`}></i>
                              </div>
                              <div>
                                <h4 className={`text-sm font-bold ${colors.textClass}`}>{colors.name}</h4>
                                <p className="text-xs text-muted-foreground">{inst?.name || 'Instituição'}</p>
                              </div>

                              <div className="ml-auto flex flex-col gap-1">
                                {isDetailComplete(a) ? (
                                  <Badge className="bg-[#00FF00] text-white">Detalhado</Badge>
                                ) : (
                                  <Badge className="bg-[#FF0000] text-white">Falta detalhar</Badge>
                                )}
                                {a.dispatchStatus === 'aprovada' && (
                                  <Badge className="bg-emerald-600 text-white text-xs">✓ Aprovada</Badge>
                                )}
                                {a.dispatchStatus === 'retificar' && (
                                  <Badge className="bg-amber-500 text-white text-xs">↻ Retificar</Badge>
                                )}
                                {a.dispatchStatus === 'retificar' && a.dispatchNote && (
                                  <p className="text-xs text-amber-600 mt-1 font-medium bg-amber-50 p-1 rounded border border-amber-100">
                                    Motivo: {a.dispatchNote}
                                  </p>
                                )}
                                {a.dispatchStatus === 'reprovada' && (
                                  <Badge className="bg-red-600 text-white text-xs">✗ Reprovada</Badge>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{a.description}</p>
                            <div className="flex justify-between items-center mt-auto">
                              <div>
                                <span className="text-lg font-bold text-accent">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(a.value || 0)}
                                </span>
                                <p className="text-xs text-muted-foreground mt-1">Emenda: {a.officialCode || a.id || '—'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-muted-foreground">Parlamentar</p>
                                <p className="text-sm font-medium text-foreground">{councilorName}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {selectedTab === 'dados' && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados da instituição</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Logo da instituição (1:1). Recomenda-se imagem quadrada (ex.: 512x512), fundo transparente, formato PNG ou SVG. Se não for quadrada, exibiremos recorte central.</p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="w-24 h-24 rounded-md border overflow-hidden bg-muted">
                          {logoPreview || logoUrl ? (
                            <img src={(logoPreview || logoUrl) as string} alt="Logo" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Prévia</div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input type="file" accept="image/*" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !institutionId) return;
                            const user = auth.currentUser;
                            const email = user?.email || undefined;
                            setUploadStatus('Validando permissões...');
                            try {
                              const tk = await getIdTokenResult(user!);
                              const role = (tk.claims?.role as string) || localStorage.getItem('userType') || '';
                              const claimInst = (tk.claims as any)?.institutionId as string | undefined;
                              if (role !== 'instituicao') {
                                toast({ title: 'Permissão ausente', description: 'Você precisa estar autenticado como Instituição para enviar imagens.', variant: 'destructive' });
                                setUploadStatus('Permissão ausente');
                                await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'role-not-instituicao', dateTime: new Date().toISOString() });
                                return;
                              }
                              const canUpload = await getUserCanUploadImages(user!.uid);
                              if (!canUpload) {
                                toast({ title: 'Permissão negada', description: 'Seu perfil não possui permissão de upload de imagens.', variant: 'destructive' });
                                setUploadStatus('Permissão negada');
                                await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'canUploadImages=false', dateTime: new Date().toISOString() });
                                return;
                              }
                              if (!claimInst || claimInst !== institutionId) {
                                let mapped = '';
                                try { mapped = (await FirestoreService.getUserInstitutionId(user!.uid)) || ''; } catch { }
                                if (!mapped) {
                                  try { const rest = await FirestoreService.restGetUserMapping(user!.uid); mapped = rest?.institutionId || ''; } catch { }
                                }
                                if (mapped !== institutionId) {
                                  try {
                                    const adminOk = await adminHealth();
                                    if (!adminOk) {
                                      toast({ title: 'Serviço administrativo indisponível', description: 'Não foi possível ajustar seu vínculo automaticamente. Tente sair e entrar novamente. Se persistir, peça para um administrador corrigir seu acesso.', variant: 'destructive' });
                                      setUploadStatus('Admin API indisponível');
                                      await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'admin-api-unavailable', dateTime: new Date().toISOString() });
                                      return;
                                    }
                                    const idToken = await user?.getIdToken();
                                    if (idToken) {
                                      const resp = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
                                      if (resp.ok) {
                                        await user?.getIdToken(true);
                                      } else {
                                        if (email) {
                                          try {
                                            const linkResp = await fetch(`${adminBase}/link-user`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, role: 'instituicao' }) });
                                            if (linkResp.ok) {
                                              const idToken2 = await user?.getIdToken();
                                              const resp2 = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken2}` } });
                                              if (resp2.ok) {
                                                await user?.getIdToken(true);
                                              } else {
                                                toast({ title: 'Permissão negada', description: 'Seu usuário não está vinculado à instituição correta. Peça a um administrador para ajustar seu acesso.', variant: 'destructive' });
                                                setUploadStatus('Vínculo de instituição ausente');
                                                await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'ensure-claims-failed', dateTime: new Date().toISOString() });
                                                return;
                                              }
                                            } else {
                                              toast({ title: 'Permissão negada', description: 'Seu usuário não está vinculado à instituição correta. Peça a um administrador para ajustar seu acesso.', variant: 'destructive' });
                                              setUploadStatus('Vínculo de instituição ausente');
                                              await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'link-user-failed', dateTime: new Date().toISOString() });
                                              return;
                                            }
                                          } catch {
                                            toast({ title: 'Permissão negada', description: 'Seu usuário não está vinculado à instituição correta. Peça a um administrador para ajustar seu acesso.', variant: 'destructive' });
                                            setUploadStatus('Vínculo de instituição ausente');
                                            await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'link-user-error', dateTime: new Date().toISOString() });
                                            return;
                                          }
                                        } else {
                                          toast({ title: 'Permissão negada', description: 'Seu usuário não está vinculado à instituição correta. Peça a um administrador para ajustar seu acesso.', variant: 'destructive' });
                                          setUploadStatus('Vínculo de instituição ausente');
                                          await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'ensure-claims-failed', dateTime: new Date().toISOString() });
                                          return;
                                        }
                                      }
                                    }
                                  } catch {
                                    toast({ title: 'Permissão negada ou configuração inválida', description: 'Verifique seu acesso à instituição ou tente novamente mais tarde.', variant: 'destructive' });
                                    setUploadStatus('Erro ao atualizar permissões');
                                    await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'ensure-claims-aborted', dateTime: new Date().toISOString() });
                                    return;
                                  }
                                }
                              }
                            } catch { }
                            const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
                            const maxSizeBytes = 2 * 1024 * 1024;
                            if (!allowedTypes.includes(file.type)) {
                              toast({ title: 'Formato não suportado', description: 'Aceitamos PNG, JPG, WebP ou SVG.', variant: 'destructive' });
                              setUploadStatus('Formato inválido');
                              await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'invalid-type', dateTime: new Date().toISOString() });
                              return;
                            }
                            if (file.size > maxSizeBytes) {
                              toast({ title: 'Arquivo muito grande', description: 'Limite de 2 MB por imagem.', variant: 'destructive' });
                              setUploadStatus('Tamanho excedido');
                              await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'file-too-large', dateTime: new Date().toISOString() });
                              return;
                            }
                            setUploadStatus('Processando imagem...');
                            const dataUrl = await new Promise<string>((resolve) => {
                              const reader = new FileReader();
                              reader.onload = () => resolve(String(reader.result));
                              reader.readAsDataURL(file);
                            });
                            const img = document.createElement('img');
                            await new Promise<void>((resolve) => { img.onload = () => resolve(); img.src = dataUrl; });
                            const size = Math.min(img.width, img.height);
                            const sx = (img.width - size) / 2;
                            const sy = (img.height - size) / 2;
                            const canvas = document.createElement('canvas');
                            canvas.width = size;
                            canvas.height = size;
                            const ctx = canvas.getContext('2d');
                            let blob: Blob = file;
                            if (ctx) {
                              ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
                              blob = await new Promise((resolve) => canvas.toBlob((b) => resolve((b as Blob) || file), 'image/webp', 0.9));
                            }
                            const path = `institutions/${institutionId}/logo.webp`;
                            try {
                              setUploadStatus('Enviando...');
                              const sref = storageRef(storage, path);
                              await uploadBytes(sref, blob, { contentType: 'image/webp', cacheControl: 'public,max-age=31536000' });
                              const url = await getDownloadURL(sref);
                              setLogoPreview(url);
                              setLogoUrl(url);
                              await updateInstitution(institutionId, { logoUrl: url });
                              toast({ title: 'Logo atualizada' });
                              setUploadStatus('Upload concluído');
                              await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'success', dateTime: new Date().toISOString() });
                            } catch {
                              try {
                                const idToken = await auth.currentUser?.getIdToken();
                                if (idToken) {
                                  const resp = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
                                  if (resp.ok) {
                                    await auth.currentUser?.getIdToken(true);
                                    const sref2 = storageRef(storage, path);
                                    await uploadBytes(sref2, blob, { contentType: 'image/webp', cacheControl: 'public,max-age=31536000' });
                                    const url2 = await getDownloadURL(sref2);
                                    setLogoPreview(url2);
                                    setLogoUrl(url2);
                                    await updateInstitution(institutionId, { logoUrl: url2 });
                                    toast({ title: 'Logo atualizada' });
                                    setUploadStatus('Upload concluído');
                                    await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'success', dateTime: new Date().toISOString() });
                                    return;
                                  }
                                }
                              } catch { }
                              try {
                                if (email) {
                                  const linkResp = await fetch(`${adminBase}/link-user`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, role: 'instituicao' }) });
                                  if (linkResp.ok) {
                                    const idToken3 = await auth.currentUser?.getIdToken();
                                    const ensure = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken3}` } });
                                    if (ensure.ok) {
                                      await auth.currentUser?.getIdToken(true);
                                      const sref3 = storageRef(storage, path);
                                      await uploadBytes(sref3, blob, { contentType: 'image/webp', cacheControl: 'public,max-age=31536000' });
                                      const url3 = await getDownloadURL(sref3);
                                      setLogoPreview(url3);
                                      setLogoUrl(url3);
                                      await updateInstitution(institutionId, { logoUrl: url3 });
                                      toast({ title: 'Logo atualizada' });
                                      setUploadStatus('Upload concluído');
                                      await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'success', dateTime: new Date().toISOString() });
                                      return;
                                    }
                                  }
                                }
                              } catch { }
                              toast({ title: 'Falha ao enviar logo', description: 'Permissão negada ou configuração inválida. Verifique seu acesso à instituição ou tente novamente mais tarde.', variant: 'destructive' });
                              setUploadStatus('Falha no upload');
                              await logUploadAttempt({ uid: user?.uid, email, institutionId, fileName: file.name, fileSize: file.size, contentType: file.type, outcome: 'failure', reason: 'upload-error', dateTime: new Date().toISOString() });
                            }
                          }} />
                          {uploadStatus && (<p className="text-xs text-muted-foreground">{uploadStatus}</p>)}
                          <Button
                            variant="outline"
                            onClick={async () => {
                              setLogoPreview(null);
                              setLogoUrl('');
                              if (institutionId) {
                                await updateInstitution(institutionId, { logoUrl: '' });
                                toast({ title: 'Logo removida' });
                              }
                            }}
                          >
                            <i className="fas fa-undo mr-2" />
                            Reverter para padrão
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="inst-link" className="text-sm text-muted-foreground">Site/Link</label>
                      <Input id="inst-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://exemplo.com" />
                    </div>
                    <div>
                      <label htmlFor="inst-contact-email" className="text-sm text-muted-foreground">Contato (email)</label>
                      <Input id="inst-contact-email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="email@exemplo.com" />
                    </div>
                    <div>
                      <label htmlFor="inst-contact-phone" className="text-sm text-muted-foreground">Contato (telefone)</label>
                      <Input id="inst-contact-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(00) 00000-0000" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Facebook</label>
                        <Input value={socialFacebook} onChange={(e) => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/seuperfil" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Instagram</label>
                        <Input value={socialInstagram} onChange={(e) => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/seuusuario" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">X (Twitter)</label>
                        <Input value={socialX} onChange={(e) => setSocialX(e.target.value)} placeholder="https://x.com/seuusuario" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">TikTok</label>
                        <Input value={socialTiktok} onChange={(e) => setSocialTiktok(e.target.value)} placeholder="https://tiktok.com/@seuusuario" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">YouTube</label>
                        <Input value={socialYoutube} onChange={(e) => setSocialYoutube(e.target.value)} placeholder="https://youtube.com/@seucanal" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">WhatsApp</label>
                        <Input value={socialWhatsapp} onChange={(e) => setSocialWhatsapp(e.target.value)} placeholder="https://wa.me/5500000000000" />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!institutionId) return;
                        const payload: Partial<Institution> = {
                          link,
                          contact: { email: contactEmail, phone: contactPhone },
                          logoUrl,
                          social: {
                            facebook: socialFacebook,
                            instagram: socialInstagram,
                            x: socialX,
                            tiktok: socialTiktok,
                            youtube: socialYoutube,
                            whatsapp: socialWhatsapp,
                          },
                        };
                        await updateInstitution(institutionId, payload);
                        toast({ title: 'Alterações salvas' });
                      }}
                    >
                      <i className="fas fa-save mr-2" />
                      Salvar alterações
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            {selectedTab === 'seguranca' && (
              <Card>
                <CardHeader>
                  <CardTitle>Segurança</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-w-md">
                    {localStorage.getItem('forcePasswordChange') === 'true' && (
                      <div className="p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm">
                        É obrigatório atualizar sua senha antes de continuar.
                      </div>
                    )}
                    <div>
                      <label htmlFor="cur-pass" className="text-sm text-muted-foreground">Senha atual</label>
                      <Input id="cur-pass" type="password" value={currentPass} onChange={(e) => setCurrentPass(e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="new-pass" className="text-sm text-muted-foreground">Nova senha</label>
                      <Input id="new-pass" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="confirm-pass" className="text-sm text-muted-foreground">Confirmar nova senha</label>
                      <Input id="confirm-pass" type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} />
                    </div>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          if (!auth.currentUser || !auth.currentUser.email) return;
                          const minLen = newPass.length >= 8;
                          const hasUpper = /[A-Z]/.test(newPass);
                          const hasLower = /[a-z]/.test(newPass);
                          const hasDigit = /\d/.test(newPass);
                          const hasSymbol = /[^A-Za-z0-9]/.test(newPass);
                          if (!newPass || newPass !== confirmPass) { toast({ title: 'Confirmação inválida', description: 'As senhas não coincidem.' }); return; }
                          if (!(minLen && hasUpper && hasLower && hasDigit && hasSymbol)) {
                            toast({ title: 'Senha fraca', description: 'Use ao menos 8 caracteres, com maiúsculas, minúsculas, dígito e símbolo.' });
                            return;
                          }
                          const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPass);
                          await reauthenticateWithCredential(auth.currentUser, cred);
                          await updatePassword(auth.currentUser, newPass);
                          setCurrentPass(''); setNewPass(''); setConfirmPass('');
                          toast({ title: 'Senha atualizada' });
                          try {
                            const uid = auth.currentUser?.uid as string;
                            await setDoc(doc(db, 'users', uid), { forcePasswordChange: false }, { merge: true });
                          } catch { }
                          localStorage.removeItem('forcePasswordChange');
                          try {
                            await FirestoreService.logAdminAction({ actorUid: auth.currentUser!.uid, actorEmail: auth.currentUser!.email || undefined, action: 'update', entity: 'user', entityId: auth.currentUser!.uid, entityName: auth.currentUser!.email || undefined, dateTime: new Date().toISOString() });
                          } catch { }
                        } catch (e) {
                          toast({ title: 'Falha ao atualizar senha', description: 'Verifique a senha atual e tente novamente.' });
                        }
                      }}
                    >
                      <i className="fas fa-key mr-2" />
                      Alterar senha
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <Dialog open={detailOpen} onOpenChange={(o) => { setDetailOpen(o); if (!o) { setDetailForId(null); setAttachments([]); setDetailSuccess(false); setDetailDisabled(false); } else { setDetailSuccess(false); setDetailDisabled(false); } }}>
              <DialogContent className="sm:max-w-[80vw] max-w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
                <div className="p-6 pb-2">
                  <DialogHeader>
                    <DialogTitle className="text-foreground">Formulário detalhamento de emenda parlamentares</DialogTitle>
                    <DialogDescription>Preencha informações para esta emenda</DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    {(() => {
                      const currentAmendment = amendments.find(x => x.id === detailForId);
                      if (!currentAmendment) return null;

                      return (
                        <>
                          {currentAmendment.dispatchStatus === 'aprovada' && (
                            <div className="mb-4 p-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-900 flex items-start gap-2">
                              <i className="fas fa-check-circle mt-0.5" />
                              <div className="flex-1">
                                <p className="font-semibold text-sm">Emenda Aprovada pelo Gestor</p>
                                {currentAmendment.dispatchNote && (
                                  <p className="text-sm mt-1">{currentAmendment.dispatchNote}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {currentAmendment.dispatchStatus === 'retificar' && (
                            <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 flex items-start gap-2">
                              <i className="fas fa-exclamation-triangle mt-0.5" />
                              <div className="flex-1">
                                <p className="font-semibold text-sm">Devolvida para Retificação</p>
                                {currentAmendment.dispatchNote && (
                                  <p className="text-sm mt-1"><strong>Motivo:</strong> {currentAmendment.dispatchNote}</p>
                                )}
                                <p className="text-xs mt-2 italic">Por favor, corrija as informações conforme solicitado e salve novamente.</p>
                              </div>
                            </div>
                          )}
                          {currentAmendment.dispatchStatus === 'reprovada' && (
                            <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-900 flex items-start gap-2">
                              <i className="fas fa-times-circle mt-0.5" />
                              <div className="flex-1">
                                <p className="font-semibold text-sm">Emenda Reprovada pelo Gestor</p>
                                {currentAmendment.dispatchNote && (
                                  <p className="text-sm mt-1">{currentAmendment.dispatchNote}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {detailSuccess && (
                      <div className="mb-4 p-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-900 flex items-center">
                        <i className="fas fa-check-circle mr-2" />
                        <span className="text-sm">Formulário enviado com sucesso</span>
                      </div>
                    )}
                    {isDeadlinePassed && !detailingSaved && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar' && (
                      <div className="mb-4 p-3 rounded border border-destructive/20 bg-destructive/10 text-destructive flex items-center">
                        <i className="fas fa-lock mr-2" />
                        <span className="text-sm">O prazo para edição expirou. Não é possível alterar este formulário.</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6">
                  <div className="pb-6 space-y-6">
                    <section className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground">DETALHAMENTO DO OBJETO</h4>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">• CLASSIFICAÇÃO DA EMENDA</label>
                        <div role="radiogroup" className="flex flex-wrap items-center gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="classificacao" value="Custeio" checked={classification === 'Custeio'} onChange={() => setClassification('Custeio')} disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} required />
                            Custeio
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name="classificacao" value="Investimento" checked={classification === 'Investimento'} onChange={() => setClassification('Investimento')} disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} required />
                            Investimento
                          </label>
                        </div>
                      </div>

                      <p id="hint-obj" className="text-xs text-muted-foreground">de forma clara e precisa descreva para qual finalidade a Emenda está sendo destinada (máximo 400 caracteres)</p>
                      <Textarea aria-describedby="hint-obj" rows={6} value={detailObject} onChange={(e) => setDetailObject(e.target.value.slice(0, 400))} placeholder="Descreva a finalidade da emenda" disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} />
                    </section>
                    <section className="space-y-3">
                      <h4 className="text-sm font-medium text-foreground">PLANO DE TRABALHO</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm text-foreground">NOME DO PROJETO ou AÇÃO</label>
                          <Input value={planProjectName} onChange={(e) => setPlanProjectName(e.target.value.slice(0, 40))} placeholder="Nome que resume a atividade (máximo 40)" disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <label className="text-sm text-foreground">FINALIDADE DE USO</label>
                          <Textarea rows={6} value={planPurpose} onChange={(e) => setPlanPurpose(e.target.value.slice(0, 400))} placeholder="Como o valor será usado (máximo 400)" disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <label className="text-sm text-foreground">PRAZO</label>
                          <div className="flex flex-wrap items-center gap-3 mt-1">
                            <Button aria-pressed={planPeriodMode === 'ano'} variant={planPeriodMode === 'ano' ? 'default' : 'outline'} onClick={() => setPlanPeriodMode('ano')} disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')}>Ano todo de 2026</Button>
                            <Button aria-pressed={planPeriodMode === 'meses'} variant={planPeriodMode === 'meses' ? 'default' : 'outline'} onClick={() => setPlanPeriodMode('meses')} disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')}>Meses de 2026</Button>
                          </div>
                          {planPeriodMode === 'meses' && (
                            <div className="mt-2">
                              <Input value={planMonths} onChange={(e) => setPlanMonths(e.target.value)} placeholder="Ex.: jan, fev, mar…" disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} />
                              <p className="text-xs text-muted-foreground mt-1">Se em período menor, cite os meses de 2026</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                    <section className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground">ANEXOS</h4>
                      <p className="text-xs text-muted-foreground">Anexe aqui mais informações que achar necessárias (ex.: Plano de Ação ou Projeto)</p>
                      <Input type="file" multiple disabled={detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')} onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length || !institutionId || !detailForId) return;
                        setAttachmentsUploading(true);
                        try {
                          const urls: string[] = [];
                          for (const f of files) {
                            const path = `institutions/${institutionId}/amendments/${detailForId}/attachments/${encodeURIComponent(f.name)}`;
                            const sref = storageRef(storage, path);
                            await uploadBytes(sref, f);
                            const url = await getDownloadURL(sref);
                            urls.push(url);
                          }
                          setAttachments(urls);
                          toast({ title: 'Anexos enviados' });
                        } finally {
                          setAttachmentsUploading(false);
                        }
                      }} />
                      {attachments.length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">{attachments.length} arquivo(s) anexado(s)</div>
                      )}
                    </section>
                  </div>
                </div>
                <div className="p-6 pt-2 border-t mt-auto">
                  <DialogFooter className="gap-2 sm:gap-0">
                    {!detailingSaved && (
                      <Button variant="outline" onClick={async () => {
                        if (!detailForId) return;
                        const per = planPeriodMode === 'ano' ? '2026' : `2026 (${(planMonths || '').trim()})`;
                        const payload = {
                          objectDetail: detailObject,
                          workPlan: { projectName: planProjectName, purpose: planPurpose, period: per },
                          attachments,
                          classification,
                        };
                        try {
                          await FirestoreService.updateAmendment(detailForId, payload);
                          setAmendments(prev => prev.map(x => x.id === detailForId ? { ...x, ...payload } as Amendment : x));
                          toast({ title: 'Rascunho salvo', description: 'Informações salvas. Você pode continuar editando depois.' });
                          setDetailOpen(false);
                        } catch (e) {
                          toast({ title: 'Erro ao salvar rascunho', description: 'Tente novamente.', variant: 'destructive' });
                        }
                      }} disabled={attachmentsUploading || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')}>Salvar Rascunho</Button>
                    )}
                    <Button onClick={() => {
                      if (!detailForId) return;
                      const obj = detailObject.trim();
                      const proj = planProjectName.trim();
                      const purp = planPurpose.trim();
                      if (!obj || obj.length < 3 || obj.length > 400) {
                        toast({ title: 'Objeto inválido', description: 'Descreva a finalidade entre 3 e 400 caracteres.', variant: 'destructive' });
                        return;
                      }
                      if (!proj || proj.length < 3 || proj.length > 40) {
                        toast({ title: 'Projeto inválido', description: 'Informe o nome do projeto entre 3 e 40 caracteres.', variant: 'destructive' });
                        return;
                      }
                      if (!purp || purp.length < 3 || purp.length > 400) {
                        toast({ title: 'Finalidade inválida', description: 'Descreva a finalidade entre 3 e 400 caracteres.', variant: 'destructive' });
                        return;
                      }
                      if (planPeriodMode === 'meses' && !(planMonths || '').trim()) {
                        toast({ title: 'Período inválido', description: 'Informe os meses de 2026.', variant: 'destructive' });
                        return;
                      }
                      if (!classification) {
                        toast({ title: 'Classificação obrigatória', description: 'Selecione Custeio ou Investimento.', variant: 'destructive' });
                        return;
                      }
                      setConfirmSubmitOpen(true);
                    }} disabled={attachmentsUploading || detailDisabled || (isDeadlinePassed && amendments.find(x => x.id === detailForId)?.dispatchStatus !== 'retificar')}>
                      {detailingSaved ? 'Detalhamento Enviado' : 'Salvar e Trancar'}
                    </Button>
                  </DialogFooter>
                </div>
                <AlertDialog open={confirmSubmitOpen} onOpenChange={setConfirmSubmitOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar envio definitivo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Após salvar e trancar, você não poderá mais alterar as informações deste detalhamento.
                        Certifique-se de que todos os dados estão corretos.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={async () => {
                        if (!detailForId) return;
                        const obj = detailObject.trim();
                        const proj = planProjectName.trim();
                        const purp = planPurpose.trim();
                        const per = planPeriodMode === 'ano' ? '2026' : `2026 (${(planMonths || '').trim()})`;

                        const payload: Partial<Amendment> & { detailingSaved?: boolean } = {
                          objectDetail: obj,
                          workPlan: { projectName: proj, purpose: purp, period: per },
                          attachments,
                          classification,
                          detailingSaved: true,
                          // Limpar status de retificação ao salvar novamente
                          ...(amendments.find(x => x.id === detailForId)?.dispatchStatus === 'retificar' ? { dispatchStatus: null as any, status: 'Detalhamento reenviado' } : {}),
                        };
                        setDetailDisabled(true);
                        try {
                          try {
                            const user = auth.currentUser;
                            if (user && institutionId) {
                              const tokenInfo = await user.getIdTokenResult();
                              const claimInst = (tokenInfo?.claims as unknown as { institutionId?: string })?.institutionId;
                              if (!claimInst || claimInst !== institutionId) {
                                const idToken = await user.getIdToken();
                                const { adminPost } = await import('@/services/admin-api');
                                const resp = await adminPost('/ensure-claims', {}, { headers: { Authorization: `Bearer ${idToken}` } });
                                if (resp.ok) {
                                  await user.getIdToken(true);
                                } else {
                                  toast({ title: 'Sem permissão para salvar detalhamento', description: 'Seu usuário não está vinculado à instituição correta.', variant: 'destructive' });
                                  setDetailDisabled(false);
                                  return;
                                }
                              }
                            }
                          } catch (e) {
                            console.warn('Falha ao verificar/vincular claims da instituição', e);
                          }
                          await FirestoreService.updateAmendment(detailForId, payload);

                          // Log history
                          const actorUid = auth.currentUser?.uid || 'system';
                          const actorEmail = auth.currentUser?.email || undefined;

                          const historyChanges = [
                            { field: 'detailingSaved', from: false, to: true },
                            { field: 'status', from: 'Em andamento', to: payload.status || 'Detalhamento enviado' }
                          ];

                          // Se estava em retificar, logar a limpeza do status
                          if (amendments.find(x => x.id === detailForId)?.dispatchStatus === 'retificar') {
                            historyChanges.push({ field: 'dispatchStatus', from: 'retificar', to: null });
                          }

                          await FirestoreService.logAmendmentHistory(detailForId, {
                            actorUid,
                            actorEmail,
                            dateTime: new Date().toISOString(),
                            changes: historyChanges
                          });

                          setAmendments(prev => prev.map(x => x.id === detailForId ? { ...x, ...payload } as Amendment : x));
                          toast({ title: 'Lançamento salvo', description: 'Seu detalhamento foi registrado com sucesso.' });
                          setDetailSuccess(true);
                          setDetailingSaved(true);
                          setDetailOpen(false);
                          setConfirmSubmitOpen(false);
                        } catch (err: any) {
                          console.error('Erro ao salvar detalhamento', err);
                          const msg = typeof err?.message === 'string' ? err.message : 'Tente novamente mais tarde.';
                          toast({ title: 'Erro ao salvar detalhamento', description: msg, variant: 'destructive' });
                          setDetailDisabled(false);
                          setConfirmSubmitOpen(false);
                        }
                      }}>Confirmar e Trancar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
